import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { AppContext } from "../context/AppContext";
import "./LoginPage.css";
import logo from "../static/media/logo.webp";
import "../index.css";

const getCSRFTokenFromCookie = () => {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) {
      return decodeURIComponent(t.substring(name.length + 1));
    }
  }
  return "";
};

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { loginUser, logoutUser } = useContext(AppContext);

  useEffect(() => {
    document.title = "FactuSysDR | Iniciar sesión";
    window.scrollTo(0, 0);
  }, []);

  const fetchCSRFToken = async () => {
    const fromCookie = getCSRFTokenFromCookie();
    if (fromCookie) return fromCookie;

    try {
      const res = await fetch("/sistema/get-csrf-token/", {
        method: "GET",
        credentials: "include",
      });

      if (!res.ok) {
        return null;
      }

      const data = await res.json().catch(() => ({}));
      return getCSRFTokenFromCookie() || data.csrfToken || data.csrf || data.token || null;
    } catch {
      return null;
    }
  };

  const saveUserSession = (data) => {
    loginUser({
      email: data.email,
      username: data.username,
      isSuperuser: data.is_superuser,
      isAdmin: data.is_admin,
      isVendedor: data.is_vendedor,
    });
  };

  const clearUserSession = () => {
    logoutUser();
  };

  const redirectByUser = (data) => {
    const isSystemUser =
      data.is_superuser || data.is_admin || data.is_vendedor;

    if (!isSystemUser) {
      clearUserSession();
      setError("Este usuario no tiene acceso al sistema.");
      return;
    }

    saveUserSession(data);

    if (data.requiere_cambio_clave) {
      navigate("/system/update-password", { state: { email: data.email } });
      return;
    }

    navigate("/admin-panel", { replace: true });
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (loading) return;

    if (!formData.email || !formData.password) {
      setError("Completa tu correo y contraseña.");
      return;
    }

    setLoading(true);

    const csrfToken = await fetchCSRFToken();

    if (!csrfToken) {
      setLoading(false);
      setError("No se pudo obtener el token CSRF.");
      return;
    }

    try {
      const response = await fetch("/sistema/login/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        clearUserSession();
        setError(data.error || "Credenciales incorrectas.");
        return;
      }

      redirectByUser(data);
    } catch {
      clearUserSession();
      setError("Error de conexión con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <section className="login-brand-panel">
          <div className="login-brand-content">
            <img src={logo} alt="FactuSysDR" className="login-brand-logo" />

            <div className="login-brand-copy">
              <Typography component="h1" className="login-brand-title">
                Una plataforma empresarial para facturación, inventario y ventas
                con una experiencia clara, moderna y lista para crecer con tu
                operación.
              </Typography>

              <Typography className="login-brand-text">
                FactuSysDR reúne procesos comerciales y administrativos en un
                entorno profesional que facilita el control diario, mejora la
                visibilidad del negocio y acompaña el trabajo de cada equipo con
                mayor orden.
              </Typography>
            </div>

            <div className="login-brand-grid">
              <div className="login-brand-card">
                <Typography className="login-brand-card-title">
                  Gestión unificada
                </Typography>
                <Typography className="login-brand-card-text">
                  Centraliza información clave para que ventas, inventario y
                  facturación se conecten de forma natural.
                </Typography>
              </div>

              <div className="login-brand-card">
                <Typography className="login-brand-card-title">
                  Flujo más eficiente
                </Typography>
                <Typography className="login-brand-card-text">
                  Reduce pasos innecesarios y facilita operaciones diarias con
                  una interfaz limpia y profesional.
                </Typography>
              </div>

              <div className="login-brand-card">
                <Typography className="login-brand-card-title">
                  Imagen de confianza
                </Typography>
                <Typography className="login-brand-card-text">
                  Proyecta solidez ante cada cliente con una plataforma sobria,
                  consistente y preparada para escalar.
                </Typography>
              </div>
            </div>
          </div>
        </section>

        <section className="login-form-panel">
          <Container maxWidth={false} className="login-form-wrap">
            <Box className="login-form-box">
              <Typography component="h2" className="login-form-title">
                Inicio de sesión
              </Typography>

              <Typography className="login-form-subtitle">
                Ingresa con tus credenciales para acceder al sistema.
              </Typography>

              {error && <Typography className="error-message">{error}</Typography>}

              <form onSubmit={handleSubmit} className="login-form-fields">
                <div className="login-field-group">
                  <Typography className="login-field-label">
                    Usuario o correo electrónico
                  </Typography>

                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="username"
                    disabled={loading}
                    InputProps={{
                      className: "input-field",
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutlineIcon className="field-icon" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                <div className="login-field-group">
                  <Typography className="login-field-label">
                    Contraseña
                  </Typography>

                  <TextField
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    size="small"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    disabled={loading}
                    InputProps={{
                      className: "input-field",
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon className="field-icon" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            disabled={loading}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="small"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? "Ingresando..." : "Iniciar sesión"}
                </Button>

                <Box className="login-legal">
                  <Typography className="login-legal-text">
                    Al ingresar a FactuSysDR, estás aceptando nuestros{" "}
                    <Link to="/terminos-condiciones" className="login-legal-link">
                      Términos y condiciones
                    </Link>{" "}
                    y{" "}
                    <Link to="/politicas-privacidad" className="login-legal-link">
                      Políticas de privacidad
                    </Link>
                    .
                  </Typography>
                </Box>
              </form>
            </Box>
          </Container>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;